/**
 * Movement Activity Attribute Rules
 *
 * Defines attribute behavior for movement activities (delivery/collection)
 * Supports to/from sites, temperature, pressure, and fill level tracking
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { AttributeConfig } from "../types"
import {
  pressureForStarted,
  pressureForCompleted,
  pressureHidden,
  temperatureForStarted,
  temperatureForCompleted,
  temperatureHidden,
  fillLevelForStarted,
  fillLevelHidden,
  quantityHidden,
  baseNoteConfig,
  getDefaultAsset,
  batchInherited,
  quantityForScheduled,
} from "../shared"
import { findAttributeValue } from "../shared/helpers"

/**
 * Asset configuration for Scheduled status
 * Editable during creation, read-only when editing existing activity
 */
const assetConfigForScheduled: AttributeConfig = {
  attribute: "asset",
  label: "Asset",
  visibility: "visible",
  // Only editable when creating a new activity (temp ID), not when editing existing
  editable: (ctx) => ctx.activity.id.startsWith("temp"),
  required: true,
  helpText: "Select an asset for this movement",
  order: 1,
  defaultValue: (ctx) => {
    return getDefaultAsset(ctx)
  },
}

/**
 * Asset configuration for non-scheduled statuses (Started/Completed)
 * Inherited chronologically from previous status, read-only
 */
const assetConfigInherited = (fromEventKind: gqlTypes.ActivityEventKind): AttributeConfig => ({
  attribute: "asset",
  label: "Asset",
  visibility: "visible",
  editable: false,
  required: true,
  helpText: "Asset for this movement (use Edit Asset button to change)",
  order: 1,
  defaultValue: (ctx) => {
    const assetValue = findAttributeValue(ctx, "asset", [fromEventKind])
    if (assetValue) return assetValue
    return getDefaultAsset(ctx)
  },
})

/**
 * "From" site configuration for Scheduled status (origin/collection point)
 */
const siteFromForScheduled: AttributeConfig = {
  attribute: gqlTypes.ActivityAttributeKind.Site,
  label: "From Site",
  visibility: "visible",
  editable: true,
  required: false,
  helpText: "Origin site (where asset is collected from)",
  order: 2,
  defaultValue: () => null,
}

/**
 * Site inherited from previous status
 */
const siteInherited = (
  label: string,
  siteKind:
    | gqlTypes.ActivityAttributeKind.Site
    | gqlTypes.ActivityAttributeKind.Source
    | gqlTypes.ActivityAttributeKind.Destination,
  fromEventKind: gqlTypes.ActivityEventKind,
  editable: boolean = false
): AttributeConfig => ({
  attribute: siteKind,
  label,
  visibility: "visible",
  editable,
  required: false,
  helpText: editable ? `${label} (editable if not set previously)` : `${label} (inherited)`,
  order: 2,
  defaultValue: (ctx) => {
    let siteAttr = ctx.activity.attributes?.find(
      (attr) => attr.attribute === siteKind && attr.event_kind === fromEventKind
    )
    const previousEventKind =
      fromEventKind === gqlTypes.ActivityEventKind.Completed
        ? gqlTypes.ActivityEventKind.Started
        : fromEventKind === gqlTypes.ActivityEventKind.Started
        ? gqlTypes.ActivityEventKind.Scheduled
        : fromEventKind
    if (!siteAttr)
      siteAttr = ctx.activity.attributes?.find(
        (attr) => attr.attribute === siteKind && attr.event_kind === previousEventKind
      )
    return siteAttr?.site?.id || null
  },
})

/**
 * Tank level for Completed - calculated from pressure and temperature
 */
const fillLevelForCompletedCalculated: AttributeConfig = {
  ...fillLevelForStarted(),
  visibility: "calculated",
  helpText: "Final tank level (auto-calculated from pressure × temperature × volume)",
  order: 6,
}

/**
 * Attribute rules for Movement activities
 * Organized by status: Scheduled → Started → Completed
 */
export const MOVEMENT_RULES: Record<gqlTypes.ActivityEventKind, AttributeConfig[]> = {
  [gqlTypes.ActivityEventKind.Pending]: [],

  /**
   * SCHEDULED STATUS
   * Planning phase - set from/to sites, asset
   */
  [gqlTypes.ActivityEventKind.Scheduled]: [
    assetConfigForScheduled,
    siteInherited("Source Site", gqlTypes.ActivityAttributeKind.Source, gqlTypes.ActivityEventKind.Scheduled, true),
    siteInherited(
      "Destination Site",
      gqlTypes.ActivityAttributeKind.Destination,
      gqlTypes.ActivityEventKind.Scheduled,
      true
    ),
    { ...pressureHidden, order: 4 },
    { ...temperatureHidden, order: 5 },
    { ...fillLevelHidden, order: 6 },
    { ...quantityForScheduled(gqlTypes.ActivityKind.Movement, "Planned Delivery (kg)"), order: 7 },
    { ...baseNoteConfig, order: 8 },
  ],

  /**
   * STARTED STATUS
   * Movement in progress - sites inherited (editable if not set), record current readings
   * Quantity at started = collection amount (what's being picked up)
   */
  [gqlTypes.ActivityEventKind.Started]: [
    assetConfigInherited(gqlTypes.ActivityEventKind.Scheduled),
    siteInherited("Source Site", gqlTypes.ActivityAttributeKind.Source, gqlTypes.ActivityEventKind.Scheduled, true),
    siteInherited(
      "Destination Site",
      gqlTypes.ActivityAttributeKind.Destination,
      gqlTypes.ActivityEventKind.Scheduled,
      true
    ),
    { ...pressureForStarted(), order: 3 },
    { ...temperatureForStarted(), order: 4 },
    { ...fillLevelForStarted(), order: 5 },
    { ...quantityHidden, order: 6 },
    { ...baseNoteConfig, order: 7 },
    { ...batchInherited([gqlTypes.ActivityEventKind.Started], false), order: 8 },
  ],

  /**
   * COMPLETED STATUS
   * Movement complete - record final readings
   * Quantity at completed = delivery amount (what's being delivered)
   */
  [gqlTypes.ActivityEventKind.Completed]: [
    assetConfigInherited(gqlTypes.ActivityEventKind.Started),
    siteInherited("Source Site", gqlTypes.ActivityAttributeKind.Source, gqlTypes.ActivityEventKind.Started, false),
    siteInherited(
      "Destination Site",
      gqlTypes.ActivityAttributeKind.Destination,
      gqlTypes.ActivityEventKind.Started,
      true
    ),
    // {
    //   ...siteInherited("To Site", gqlTypes.ActivityEventKind.Started),
    //   order: 2,
    //   // Fallback to Scheduled if not found in Started
    //   defaultValue: (ctx) => {
    //     const startedSite = ctx.activity.attributes?.find(
    //       (attr) =>
    //         attr.attribute === gqlTypes.ActivityAttributeKind.Site &&
    //         attr.event_kind === gqlTypes.ActivityEventKind.Started
    //     )
    //     if (startedSite?.site?.id) return startedSite.site.id

    //     const scheduledSite = ctx.activity.attributes?.find(
    //       (attr) =>
    //         attr.attribute === gqlTypes.ActivityAttributeKind.Site &&
    //         attr.event_kind === gqlTypes.ActivityEventKind.Scheduled
    //     )
    //     return scheduledSite?.site?.id || null
    //   },
    // },
    { ...pressureForCompleted(), order: 3 },
    { ...temperatureForCompleted(), order: 4 },
    { ...fillLevelForCompletedCalculated, order: 5 },
    { ...quantityHidden, order: 6 },
    { ...baseNoteConfig, order: 7 },
    { ...batchInherited([gqlTypes.ActivityEventKind.Started], false), order: 8 },
  ],
}
